import { useState, useCallback, useEffect } from "react";
import { Form } from "react-router-dom";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    FormFeedback,
    FormGroup,
    FormText,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Nav,
    NavItem,
    Row,
    TabContent,
    TabPane,
} from "reactstrap";


import DataTable from '../../../components/DataTable';
import { useTheme } from "../../../hooks/useTheme";

import ModuleInputControl from "./ModuleInputControl";
import { executeLogic } from "../../flowBuilder/data/quoteGenerationService";
import { computeCondition } from "./moduleBuilderFunctions";
import { getDefaultInputValues } from "../../quotes/functions/quoteFunctions";

const ModuleInputsModal = ({ selectedModule, moduleRevisionId, selectedModuleRevisionLogic, isModuleInputModalOpen, setIsModuleInputModalOpen, initialInputValues, handleFormSubmit, handleFormCancelled, submitButtonLabel = 'Ok' }) => {
    const theme = useTheme();

    const formInputs = selectedModuleRevisionLogic?.form?.inputs ?? []

    const inputs = [
        {
                guid: "quoteGroupDescription",
                name: "quoteGroupDescription",
                defaultValue: selectedModule.moduleName,
                type: "text",
                label: "Quote Description",
                size: "md",
                help: "Default heading used for grouping the generated items on the quote",
                required: true
        },
        {
                guid: "isCreditItem",
                name: "isCreditItem",
                type: "checkbox",
                label: "Credit Item",
                size: "sm",
                help: "If selected, this will produce negative quantities and costs/prices.  Typically used for changing an order, or for refunds",
        },
        {
            guid: "notes",
            name: "notes",
            defaultValue: selectedModule.notes,
            type: "textarea",
            label: "Notes",
            size: "md",
            help: "Notes attached to module",
            required: true
        },
        ...selectedModuleRevisionLogic.form.inputs
    ]

    const overviewPage = {
            guid: "Overview",
            name: "Overview",
            inputs: ["quoteGroupDescription", "isCreditItem", "notes"]
    }

    // Initialize currentPage with the first page's GUID
    const [formPages, setFormPages] = useState([overviewPage]);

    const [currentPage, setCurrentPage] = useState("Overview");
    const [inputValues, setInputValues] = useState(initialInputValues ? [...initialInputValues] : getDefaultInputValues(inputs))
    const [inputValidity, setInputValidity] = useState([])
    const [inputWarnings, setInputWarnings] = useState([])

    const [calculatingQuote, setCalculatingQuote] = useState(true);
    const [flowBuilderOutputLogicResults, setFlowBuilderOutputLogicResults] = useState([]);
    const [flowBuilderOutputLogicQuoteItems, setFlowBuilderOutputLogicQuoteItems] = useState([]);
    const [flowBuilderInputLogicResults, setFlowBuilderInputLogicResults] = useState([]);
    const [processedInputCheckResultsMap, setProcessedInputCheckResultsMap] = useState()
    const [acknowledgedWarnings, setAcknowledgedWarnings] = useState([])
    const [totalWarningNotAcceptedCount, setTotalWarningNotAcknowledgedCount] = useState([])
    const [totalWarningCount, setTotalWarningCount] = useState([])

    const [settingsModalOpen, setSettingsModalOpen] = useState(false);

    const COLUMN_SETTINGS_KEY = "addModuleDialogQuoteItemsTableColumnSettings";

    const toggleSettingsModal = () => setSettingsModalOpen(!settingsModalOpen);

    const columns = [
        {
            key: "internalName",
            label: "Description",
            isDefault: true
        },
        {
            key: "quantity",
            label: "Quantity",
            isDefault: true
        },
        {
            key: "upfrontCost",
            label: "Upfront Cost"
        },
        {
            key: "upfrontPrice",
            label: "Upfront Price",
        },
        {
            key: "recurringCost",
            label: "Recurring Cost"
        },
        {
            key: "recurringPrice",
            label: "Recurring Price"
        }
    ];

    const defaultVisibleColumns = columns.reduce((acc, col) => {
        acc[col.key] = !!col.isDefault;
        return acc;
    }, {});

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const stored = localStorage.getItem(COLUMN_SETTINGS_KEY);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch {
                return defaultVisibleColumns;
            }
        }
        return defaultVisibleColumns;
    });

    const updateFormPagesToShowVisible = () => {
        let newPagesList = [
            overviewPage,
            ...selectedModuleRevisionLogic.form.pages.filter(x => pageisVisible(x))
        ]
        
        if (totalWarningCount > 0) {
            newPagesList.push(
                {
                    guid: "Warnings",
                    name: "Warnings",
                    inputs: []
                }
            )
        }

        if (inputValidity.filter(x => !x.isValid).length === 0 && totalWarningNotAcceptedCount === 0) {
            newPagesList.push(
                {
                    guid: "Review",
                    name: "Review",
                    inputs: []
                }
            )
        }

        setFormPages(newPagesList)
    }

    const executeInputLogic = async () => {
        if (selectedModuleRevisionLogic.processing.inputLogic.flows.length === 0) {
            setFlowBuilderInputLogicResults([])
            return;
        }

        var result = await executeLogic({
            logic: selectedModuleRevisionLogic.processing.inputLogic, 
            formInputs: inputs, 
            userInputs: inputValues,
            moduleRevisionId,
            moduleName: selectedModule.moduleName + " custom validation"
        })

        setFlowBuilderInputLogicResults(result.results)
    }

    const executeOutputLogic = async () => {
        var result = await executeLogic({
            logic: selectedModuleRevisionLogic.processing.outputLogic, 
            formInputs: inputs, 
            userInputs: inputValues,
            moduleRevisionId,
            moduleName: selectedModule.moduleName
        })
        setFlowBuilderOutputLogicResults(result.results)
        setFlowBuilderOutputLogicQuoteItems(result.quoteItems)
    }

    useEffect(() => {
        updateFormPagesToShowVisible()
    }, [selectedModuleRevisionLogic, acknowledgedWarnings, processedInputCheckResultsMap, totalWarningCount, totalWarningNotAcceptedCount]);

    useEffect(() => {
        setTotalWarningNotAcknowledgedCount(
            inputWarnings.reduce((total, entry) => {
                return total + (entry.warnings?.filter(x => !acknowledgedWarnings.find(acknowledgedWarning => acknowledgedWarning === x.guid)).length || 0);
            }, 0)
        )
    }, [inputWarnings, acknowledgedWarnings]);

    useEffect(() => {
        setTotalWarningCount(
            inputWarnings.reduce((total, entry) => {
                return total + (entry.warnings?.length || 0);
            }, 0)
        )
    }, [inputWarnings]);

    useEffect(() => {
        if (currentPage === "Review") {
            const runLogic = async () => {
                setCalculatingQuote(true);
                await executeOutputLogic();
                setCalculatingQuote(false);
            };

            runLogic();
        }
    }, [currentPage]);

    useEffect(() => {
        setInputValues(initialInputValues && Array.isArray(initialInputValues) && initialInputValues.length > 0 ? [...initialInputValues] : getDefaultInputValues(inputs))
    }, [initialInputValues]);

    // Reset dialog when we open or close it
    useEffect(() => {
        setCurrentPage("Overview")

        if (isModuleInputModalOpen) {
            executeInputLogic()
        }
    }, [isModuleInputModalOpen]);

    useEffect(() => {
        executeInputLogic()
        updateFormPagesToShowVisible()
    }, [inputValues]);

    useEffect(() => {
        if (flowBuilderInputLogicResults) {

            const processedRulesMap = new Map();
            flowBuilderInputLogicResults.forEach(({ flowGuid, result }) => {
                processedRulesMap.set(flowGuid, result);
            });
            setProcessedInputCheckResultsMap(processedRulesMap)
        }
        
    }, [flowBuilderInputLogicResults])

      const operandType = (operand) => {
        
        if (operand === null) {
            return "none"
        } else if (operand.startsWith("inputcheck_")) {
            return "inputcheck"
        } else if (operand.startsWith("input_")) {
            var input = inputs.find(x => x.guid === operand.replace("input_", ""))

            if (!input) return "unknown"

            switch (input.type.toLowerCase()) {
              case "checkbox":
                return "true/false"
              case "text":
              case "textarea":
                 return "text"
              case "number":
                 return "number";
              default:
                return input.type.toLowerCase()
            }
        }

        return "unknown"
    }

    const conditionResult = useCallback((condition) => {
        if (!condition) return true;

        if (condition?.operand1.startsWith('inputcheck_')) {
            if (
                !(processedInputCheckResultsMap instanceof Map) ||
                !condition?.operand1
            ) {
                return true;
            }
            return (processedInputCheckResultsMap.get(condition.operand1.replace('inputcheck_', '')) === "True")
        } else if (condition?.operand1.startsWith('input_')) {
            const operand1Value = inputValues.find(x => x.inputGuid === condition.operand1.replace('input_', ''))?.value
            let operand2Value;
            
            if (condition?.operand2?.startsWith('input_')) {
                operand2Value = inputValues.find(x => x.inputGuid === condition.operand2.replace('input_', ''))
            } else {
                operand2Value = condition.operand2
            }

            if (operandType(condition.operand1) === "number") {
                switch (condition.comparison) {
                    case "Is Equal To":
                        return Number(operand1Value) === Number(operand2Value)
                    case "Is Not Equal To":
                        return Number(operand1Value) !== Number(operand2Value)
                    case "Is Less Than":
                        return Number(operand1Value) < Number(operand2Value)
                    case "Is Greater Than":
                        return Number(operand1Value) > Number(operand2Value)
                    case "Is Less Than or Equal To":
                        return Number(operand1Value) <= Number(operand2Value)
                    case "Is Greater Than or Equal To":
                        return Number(operand1Value) >= Number(operand2Value)
                    default:
                        return true;
                }
            } else if (operandType(condition.operand1) === "true/false") {
                switch (condition.comparison) {
                    case "Is Equal To":
                        return Boolean(operand1Value) === Boolean(operand2Value)
                    case "Is Not Equal To":
                        return Boolean(operand1Value) !== Boolean(operand2Value)
                    default:
                        return true;
                }
            } else {
                switch (condition.comparison) {
                    case "Is Equal To":
                        return operand1Value === operand2Value
                    case "Is Not Equal To":
                        return operand1Value !== operand2Value
                    default:
                        return true;
                }
            }
        }
    }, [processedInputCheckResultsMap]);

    const pageisVisible = (page) => {
        const result = computeCondition(page.visibility?.condition, inputValues, inputs, processedInputCheckResultsMap) 
        //const result = conditionResult(page.visibility?.condition)
        return result
    }

    const getInputLabel = (inputGuid) => {
        var inputControl = selectedModuleRevisionLogic?.form?.inputs?.find(x => x.guid === inputGuid)
        return inputControl.label
    }

    const handleInputValueChanged = (inputGuid, newValue) => {
        setInputValues( prevState => {
            var newState = [...prevState]
            var inputValueIndex = newState.findIndex( x => x.inputGuid === inputGuid)

            if (inputValueIndex >= 0) {
                // Entry exists in the array already for this guid, so change the value
                var currentInputValue = newState[inputValueIndex]
    
                newState[inputValueIndex] = { ...currentInputValue, value: newValue}
            } else {
                // Entry doesn't exist in the array, so add it to the list
                newState.push({ inputGuid: inputGuid, value: newValue})
            }

            return newState;
        })
    }

    const handleInputWarningsChanged = (inputGuid, updatedWarningsList) => {
        setInputWarnings( prevState => {
            var newState = [...prevState]
            var inputWarningIndex = newState.findIndex( x => x.inputGuid === inputGuid)

            if (inputWarningIndex >= 0) {
                // Entry exists in the array already for this guid, so change the value
                var currentInputWarnings = newState[inputWarningIndex]
    
                newState[inputWarningIndex] = { ...currentInputWarnings, warnings: updatedWarningsList}
            } else {
                // Entry doesn't exist in the array, so add it to the list
                newState.push({ inputGuid, warnings: updatedWarningsList})
            }

            return newState;
        })
    }

    const handleInputValidityChanged = (inputGuid, newIsValid) => {
        setInputValidity( prevState => {
            var newState = [...prevState]
            var inputValidityIndex = newState.findIndex( x => x.inputGuid === inputGuid)

            if (inputValidityIndex >= 0) {
                // Entry exists in the array already for this guid, so change the value
                var currentInputValidity = newState[inputValidityIndex]
    
                if ( currentInputValidity.isValid !== newIsValid) {
                    newState[inputValidityIndex] = { ...currentInputValidity, isValid: newIsValid}
                }
            } else {
                // Entry doesn't exist in the array, so add it to the list
                newState.push({ inputGuid, isValid: newIsValid})
            }

            return newState;
        })
    }

    const submitButtonClicked = () => {
        setIsModuleInputModalOpen(false)

        const basketItems = flowBuilderOutputLogicQuoteItems.map(x => ({
            ...x,
            isCreditItem: inputValues.find(x => x.inputGuid === "isCreditItem").value,
            quoteGroupDescription: inputValues.find(x => x.inputGuid === "quoteGroupDescription").value,
            moduleId: selectedModule.id,
            moduleRevisionId: moduleRevisionId,
            source: "Module",

            // The properties below are returned as JSON, so need to be converted to arrays
            tags: JSON.parse(x.tags), 
            customAttributes: JSON.parse(x.customAttributes),
            typeCustomAttributes: JSON.parse(x.typeCustomAttributes)
        }))

        const getInputPropertiesForAttachingToWarnings = (inputGuid) => {
            const foundInput = formInputs.find(x => x.guid === inputGuid)

            if (!foundInput) return {
                inputGuid: inputGuid,
                inputLabel: "Unknown Input"
            }

            return {
                inputGuid: foundInput.guid,
                inputLabel: foundInput.label
            }
        }

        const warnings = inputWarnings.flatMap(input => 
            input.warnings.map(warning => 
                ({...warning, 
                  ...getInputPropertiesForAttachingToWarnings(input.inputGuid)
                })
            ) || []
        )

        handleFormSubmit({inputValues, basketItems, acknowledgedWarnings: warnings})
    }

    const closeDialogButtonClicked = () => {
        setIsModuleInputModalOpen(false)
        handleFormCancelled?.()
    }

    const isFirstPage = () => {
        var pageIndex = formPages.findIndex(x => x.guid === currentPage)
        return pageIndex === 0
    }

    const isLastPage = () => {
        var pageIndex = formPages.findIndex(x => x.guid === currentPage)
        return pageIndex === formPages.length - 1
    }

    const backButtonClicked = () => {
        var pageIndex = formPages.findIndex(x => x.guid === currentPage)
        setCurrentPage(formPages[pageIndex - 1].guid)        
    }

    const nextButtonClicked = () => {
        var pageIndex = formPages.findIndex(x => x.guid === currentPage)
        setCurrentPage(formPages[pageIndex + 1].guid)
    }

    const getInvalidInputCountForPage = (page) => {
        var inValidInputsOnThisPage = inputValidity.filter(x => page.inputs.find(pageInputGuid => pageInputGuid === x.inputGuid && x.isValid === false))
        return inValidInputsOnThisPage.length 
    }

    const invalidInputCount = inputValidity.filter(x => !x.isValid).length;

    return (
        <>
            <Modal isOpen={isModuleInputModalOpen} toggle={() => closeDialogButtonClicked()} centered size="xl" scrollable backdrop="static">
                <ModalHeader toggle={() => closeDialogButtonClicked()}>
                    Add {selectedModule.moduleName} to Quote
                </ModalHeader>
                <ModalBody style={{ height: '60vh' }}>
                    <Row>
                        <Nav tabs>
                            {formPages.map((page) => (
                                <NavItem
                                    key={page.guid}
                                    className={`p-3 ${currentPage === page.guid ? "active" : ""}`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setCurrentPage(page.guid)}
                                >
                                    {page.name} 
                                    {getInvalidInputCountForPage(page) > 0 && <span className="mx-2"><i className="bi bi-x-circle text-danger" ></i></span>}
                                    {page.name === "Warnings" && totalWarningNotAcceptedCount > 0 && <span> ({totalWarningNotAcceptedCount})</span>}
                                    {page.name === "Warnings" && totalWarningNotAcceptedCount === 0 && <span className="mx-2"><i className="bi bi-check-circle text-primary" ></i></span>}
                                </NavItem>
                            ))}
                        </Nav>
                    </Row>
                    <br />
                    {currentPage === "Warnings" &&
                        <>
                            <div className="h5 my-3">Please acknowledge the following warnings:</div>
                            <ul>
                            {inputWarnings.filter(x => x.warnings.length > 0).map(inputWithWarnings =>
                                <li>
                                    {getInputLabel(inputWithWarnings.inputGuid)}
                                    <ul>
                                        {inputWithWarnings.warnings.map(warning => 
                                            <li style={{listStyle:"none"}}>
                                                <i className="text-warning bi bi-exclamation-triangle me-2"></i>{warning.message}
                                                <Input
                                                    className="ms-2"
                                                    type="switch"
                                                    checked={acknowledgedWarnings.find(x => x === warning.guid) || false}
                                                    onChange={(e) => {
                                                        const isChecked = e.target.checked;

                                                        setAcknowledgedWarnings(prev => {
                                                            if (!isChecked) {
                                                                return prev.filter(x => x !== warning.guid)
                                                            } else {
                                                                return prev.includes(warning.guid) ? prev : [...prev, warning.guid];
                                                            }
                                                        });
                                                    }}
                                                />
                                            </li>
                                        )}
                                    </ul>
                                </li>    
                            )}   
                            </ul>
                        </>
                    }
                    {currentPage === "Review" &&
                        <>
                            <h3>Quote items</h3>
                            {calculatingQuote &&
                                <p>Calculating Quote...</p>
                            }
                            {!calculatingQuote &&
                                <DataTable
                                    columns={columns.filter(col => visibleColumns[col.key])}
                                    data={flowBuilderOutputLogicQuoteItems.map((x, index) => ({ ...x, id: index }))}
                                    totalRecords={flowBuilderOutputLogicQuoteItems.length}
                                    isLoading={!flowBuilderOutputLogicResults || flowBuilderOutputLogicResults.length === 0}
                                    error={flowBuilderOutputLogicResults && flowBuilderOutputLogicResults.length === 0}
                                    exportToCsv={false}
                                    sortConfiguration={{ key: "guid", direction: "ascending" }}
                                    actions={[
                                        <Button key="settings" outline color={theme === "dark-mode" ? "dark" : "light"} onClick={toggleSettingsModal}>
                                            <i className="bi bi-gear fs-5"></i>
                                        </Button>
                                    ]}
                                /> 
                            }
                        </>
                    }
                    <div>
                        {inputs.sort((input1,input2) => {
                            var currentPageObject = formPages.find(x => x.guid === currentPage)                           
                            var input1PositionInCurrentPage = currentPageObject.inputs.findIndex(x => x === input1.guid)
                            var input2PositionInCurrentPage = currentPageObject.inputs.findIndex(x => x === input2.guid)

                            if (input1PositionInCurrentPage === -1 && input1PositionInCurrentPage === -1) {
                                // Neither input is on this page, so will be hidden anyway

                                return 0; // Consider as the same with regards to order
                            } else if (input1PositionInCurrentPage === -1) {
                                // Input 1 isn't on this page, which means Input 2 must be (as we have already checked that at least one of them is on this page)

                                return 1; // input 1 should go after Input 2 that's on this page
                            } else if (input2PositionInCurrentPage === -1) {
                                // Input 2 isn't on this page, which means Input 1 must be (as we have already checked that at least one of them is on this page)

                                return -1; // input 2 should go after Input 1 that's on this page
                            } else {
                                // Both are on this page, so compare by the order they are in the page inputs list
                                return (input1PositionInCurrentPage - input2PositionInCurrentPage)
                            }
                        }).map(input =>                            
                            <Row key={input.guid} style={{ 
                                /* We want all inputs to be in the DOM so we can run validation on them, to show other tabs with invalid inputs.  We will hide the ones that aren't on current page */
                                display: formPages.find(x => x.guid === currentPage).inputs.find(x => x === input.guid) ? "flex" : "none" 
                                }}>
                                <ModuleInputControl
                                    inputGuid={input.guid}
                                    inputs={inputs} 
                                    values={inputValues}
                                    isOnVisiblePage={formPages.find(page => page.inputs.find(pageInputGuid => pageInputGuid === input.guid)) ? true : false}
                                    handleInputValueChanged={handleInputValueChanged} 
                                    handleInputValidityChanged={handleInputValidityChanged}
                                    handleInputWarningsChanged={handleInputWarningsChanged}
                                    processedRuleResultsMap={processedInputCheckResultsMap}
                                />
                            </Row>)
                        }
                    </div>
                </ModalBody>
                <ModalFooter className="justify-content-between">
                    <Row className="w-100">
                        <Col xs="auto" className="text-start mt-2">
                            {invalidInputCount > 0 
                                ?  (                                    
                                    <span>
                                        <i className="text-danger bi bi-x-circle me-2"></i>
                                        There {invalidInputCount === 1 ? "is" : "are"} {invalidInputCount} invalid input{invalidInputCount !== 1 ? "s" : ""}.  Please resolve the indicated issues.
                                    </span>
                                    ) 
                                :  totalWarningCount > 0 && acknowledgedWarnings.length < totalWarningCount
                                     ? (                                        
                                            <span>
                                                <i className="text-warning bi bi-exclamation-triangle me-2"></i>
                                                There {totalWarningCount === 1 ? "is" : "are"} {totalWarningCount} warning{totalWarningCount !== 1 ? "s" : ""}.  Please acknowledge to proceed.
                                            </span>
                                        )
                                    : currentPage !== "Review" 
                                        ?   (
                                                <span>
                                                    <i className="text-success bi bi-check-circle me-2"></i>
                                                    Inputs complete. Please proceed to "Review" tab.
                                                </span>
                                            ) 
                                        : null
                            }
                        </Col>
                        <Col className="text-end">
                            <div style={{ width: "100%" }}>
                                <Button
                                    className="me-2"
                                    color="danger"
                                    onClick={closeDialogButtonClicked}>Cancel</Button>
                                <Button
                                    className="me-2"
                                    color="secondary"
                                    disabled={isFirstPage()}
                                    onClick={backButtonClicked}>&lt; Back</Button>
                                <Button
                                    className="me-2"
                                    color="secondary"
                                    disabled={isLastPage()}
                                    onClick={nextButtonClicked}>Next &gt;</Button>
                                <Button
                                    disabled={calculatingQuote || currentPage !== "Review" || flowBuilderOutputLogicQuoteItems.length === 0}
                                    color="secondary"
                                    onClick={submitButtonClicked}>{submitButtonLabel}</Button>
                            </div>
                        </Col>
                    </Row>
                </ModalFooter>
            </Modal>

            <Modal isOpen={settingsModalOpen} toggle={toggleSettingsModal} scrollablecentered backdrop="static">
                <ModalHeader toggle={toggleSettingsModal}>Table Settings</ModalHeader>
                <ModalBody>
                    {columns.map((col) => (
                        <FormGroup switch key={col.key}>
                            <Input
                                type="switch"
                                role="switch"
                                id={`switch-${col.key}`}
                                checked={visibleColumns[col.key]}
                                disabled={col.system} // 🔒 disable if it's a system column
                                onChange={(e) => {
                                    const updated = {
                                        ...visibleColumns,
                                        [col.key]: e.target.checked
                                    };
                                    setVisibleColumns(updated);
                                    localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(updated));
                                }}
                            />
                            <Label check htmlFor={`switch-${col.key}`}>
                                {col.label || "Action"} {col.system && <small className="text-muted">(Required)</small>}
                            </Label>
                        </FormGroup>
                    ))}
                    <br />
                    <Button outline color="danger" size="sm" onClick={() => {
                        setVisibleColumns(defaultVisibleColumns);
                        localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(defaultVisibleColumns));
                    }}>
                        Reset to Defaults
                    </Button>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleSettingsModal}>Close</Button>
                </ModalFooter>
            </Modal>
            </>
    );
};

export default ModuleInputsModal;
